import { Clock } from "@lib/titan2/dist/game/control/Clock";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { MessageData } from "@lib/titan2/dist/game/model/data/MessageData";
import { CommonLang } from "@lib/titan2/dist/game/view/lang/CommonLang";
import { KeyValuePair } from "@lib/titan2/dist/util/collection/KeyValuePair";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import { Timer } from "@lib/titan2/dist/util/time/Timer";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { gsap } from "gsap";
import { Service } from "../../../../api/Service";
import { SlingoEvent } from "../../../controller/event/SlingoEvent";
import { SlingoLang } from "../lang/SlingoLang";
import { AbstractGameView } from "./AbstractGameView";
/**
 * MessageBar.ts
 */
export class MessageBar extends AbstractGameView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(CommonGameEvent.START_GAME, this.handleStartGame);
        this.registerGame(SlingoEvent.SPIN_RESULT, this.handleSpinResult);
        this.registerGame(SlingoEvent.REEL_STRIKE_JOKER, this.handleReelStrikeJoker);
        this.registerGame(SlingoEvent.REEL_STRIKE_SUPER_JOKER, this.handleReelStrikeSuperJoker);
        this.registerGame(SlingoEvent.REEL_STRIKE_DEVIL, this.handleReelStrikeDevil);
        this.registerGame(SlingoEvent.REEL_STRIKE_FREE_SPIN, this.handleReelStrikeFreeSpin);
        //this.registerGame(SlingoEvent.REEL_STRIKE_BONUS, this.handleReelStrikeBonus);
        this.registerGame(SlingoEvent.DAB_ACTIVE, this.handleDabActive);
        this.registerGame(CommonGameEvent.SPIN, this.handleStandardSpin);
        this.registerGame(CommonGameEvent.FREE_SPIN, this.handleFreeSpin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.handleAvailableWin);
        this.registerGame(SlingoEvent.SYMBOL_WIN_JOKER, this.handleScatterWinJoker);
        this.registerGame(SlingoEvent.SYMBOL_WIN_COIN, this.handleScatterWinCoin);
        this.registerGame(SlingoEvent.DEVIL_ANIMATION, this.handleDevilAnimation);
        this.registerGame(SlingoEvent.CHERUB_ANIMATION, this.handleCherubAnimation);
        this.registerGame(SlingoEvent.SLINGO, this.handleSlingo);
        this.registerGame(SlingoEvent.SLINGO_COMPLETE, this.handleSlingoComplete);
        this.registerGame(CommonGameEvent.COMPLETE, this.handleComplete);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(CommonGameEvent.STAKE_UPDATED, this.updateGameId);
        this.registerCore(CommonGameEvent.BALANCE_UPDATED, this.updateGameId);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.updateGameId);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.updateGameId);
        this.registerGame(SlingoEvent.BALANCE_UPDATE, this.updateGameId);
    }
    removeEventListeners() {
        super.removeEventListeners();
        this.unregisterGame(CommonGameEvent.PURCHASE_ENTRY);
        this.unregisterGame(CommonGameEvent.START_GAME);
        this.unregisterGame(SlingoEvent.SPIN_RESULT);
        this.unregisterGame(SlingoEvent.REEL_STRIKE_JOKER);
        this.unregisterGame(SlingoEvent.REEL_STRIKE_SUPER_JOKER);
        this.unregisterGame(SlingoEvent.REEL_STRIKE_DEVIL);
        this.unregisterGame(SlingoEvent.REEL_STRIKE_FREE_SPIN);
        this.unregisterGame(SlingoEvent.DAB_ACTIVE);
        this.unregisterGame(CommonGameEvent.SPIN);
        this.unregisterGame(CommonGameEvent.FREE_SPIN);
        this.unregisterGame(SlingoEvent.AVAILABLE_WIN);
        this.unregisterGame(SlingoEvent.SYMBOL_WIN_JOKER);
        this.unregisterGame(SlingoEvent.SYMBOL_WIN_COIN);
        this.unregisterGame(SlingoEvent.DEVIL_ANIMATION);
        this.unregisterGame(SlingoEvent.CHERUB_ANIMATION);
        this.unregisterGame(SlingoEvent.SLINGO);
        this.unregisterGame(SlingoEvent.SLINGO_COMPLETE);
        this.unregisterGame(CommonGameEvent.COMPLETE);
        this.unregisterGame(CommonGameEvent.STANDARD_SPIN_RECOVERY);
        this.unregisterGame(CommonGameEvent.FREE_SPIN_RECOVERY);
        this.unregisterGame(CommonGameEvent.STAKE_UPDATED);
        this.unregisterCore(CommonGameEvent.BALANCE_UPDATED);
        this.unregisterGame(CommonGameEvent.PURCHASE_SPIN);
        this.unregisterGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY);
        this.unregisterGame(SlingoEvent.BALANCE_UPDATE);
    }
    create() {
        super.create();
        this.data = this.factory.createMessageBarData();
        this.cycling = false;
        this.maxLength = this.uiProperties.getMaxLength();
        this.createBackground();
        this.createMessageField();
        this.createGameIdField();
        this.createMessages();
        this.createMessageTimer();
        this.createClock();
        Logger.addObjectRef(this, "MessageBar");
    }
    updateGameId() {
        if (this.gameId) {
            let gameId = this.model.getGameInstanceId();
            if (gameId > 0) {
                let params = [new KeyValuePair(CommonLang.PARAM_AMOUNT, gameId)];
                let message = this.langManager.getLang(SlingoLang.BOTTOM_BAR_GAME_ID, params);
                this.gameId.setText(message, true);
            }
            else {
                this.gameId.clear();
            }
        }
    }
    createBackground() {
        if (this.uiProperties.backgroundProperties) {
            this.background = new TitanSprite(this.landscapeProperties.backgroundProperties, this.portraitProperties?.backgroundProperties);
            this.background.x = this.renderer.getViewport().width / 2;
            this.addChild(this.background);
        }
    }
    createClock() {
        if (this.uiProperties.clockProperties) {
            this.clock = new Clock(this.landscapeProperties.clockProperties, this.portraitProperties?.clockProperties);
            this.addChild(this.clock);
        }
    }
    createMessageField() {
        this.messageField = ViewUtils.createField(this, this.landscapeProperties.messageFieldProperties, this.portraitProperties?.messageFieldProperties);
    }
    createGameIdField() {
        if (this.uiProperties.gameIdProperties) {
            this.gameId = ViewUtils.createField(this, this.landscapeProperties.gameIdProperties, this.portraitProperties?.gameIdProperties);
        }
    }
    createMessages() {
        this.idleMessageKeys = this.createMessageKeys(SlingoLang.IDLE, this.data.idleMessages);
        this.idleStandardSpinMessageKeys = this.createMessageKeys(SlingoLang.IDLE_STANDARD_SPIN, this.data.idleStandardSpinMessages);
        this.idleFreeSpinMessageKeys = this.createMessageKeys(SlingoLang.IDLE_FREE_SPIN, this.data.idleFreeSpinMessages);
        this.chooseCellMessageKeys = this.createMessageKeys(SlingoLang.CHOOSE_CELL, this.data.chooseCellMessages);
        this.chooseCellJokerMessageKeys = this.createMessageKeys(SlingoLang.CHOOSE_CELL_JOKER, this.data.chooseCellJokerMessages);
        this.chooseCellSuperJokerMessageKeys = this.createMessageKeys(SlingoLang.CHOOSE_CELL_SUPER_JOKER, this.data.chooseCellSuperJokerMessages);
    }
    createMessageTimer() {
        this.messageTimer = new Timer(this.uiProperties.messageInterval);
        this.messageTimer.onUpdate(this.handleMessageTimerUpdated, this);
    }
    handlePurchaseEntry() {
        this.setCycle(this.idleMessageKeys, this.cycleIdleMessage);
        this.updateGameId();
    }
    handleStartGame() {
        this.setSingle(this.singleMessage(SlingoLang.START_GAME));
        this.updateGameId();
    }
    handleSpinResult() {
        let state = this.model.getPreviousState();
        if (state.isStandardSpin()) {
            this.setSingle(this.singleStandardSpinMessage());
        }
        else if (state.isFreeSpin()) {
            this.setSingle(this.singleMessage(SlingoLang.FREE_SPIN));
        }
        else if (state.isPurchaseSpin()) {
            this.setSingle(this.singleMessage(SlingoLang.PURCHASED_SPIN));
        }
        this.updateGameId();
    }
    handleReelStrikeJoker() {
        this.setSingle(this.singleMessage(SlingoLang.REEL_STRIKE_JOKER));
    }
    handleReelStrikeSuperJoker() {
        this.setSingle((this.singleMessage(SlingoLang.REEL_STRIKE_SUPER_JOKER)));
    }
    handleReelStrikeDevil() {
        this.setSingle((this.singleMessage(SlingoLang.REEL_STRIKE_DEVIL + RandomUtil.nextInt(0, 2))));
    }
    handleReelStrikeFreeSpin() {
        this.setSingle((this.singleMessage(SlingoLang.REEL_STRIKE_FREE_SPIN)));
    }
    handleReelStrikeBonus() {
        this.setSingle((this.singleMessage(SlingoLang.REEL_STRIKE_BONUS)));
    }
    handleDabActive() {
        let dabData = this.model.getDabData();
        if (dabData.hasSuperJokerCellToDab()) {
            this.setCycle(this.chooseCellSuperJokerMessageKeys, this.cycleMessage);
        }
        else if (dabData.hasJokerCellToDab()) {
            this.setCycle(this.chooseCellJokerMessageKeys, this.cycleMessage);
        }
        else {
            this.setCycle(this.chooseCellMessageKeys, this.cycleMessage);
        }
    }
    handleStandardSpin() {
        this.setCycle(this.idleStandardSpinMessageKeys, this.cycleIdleStandardSpinMessage);
    }
    handleFreeSpin() {
        this.setCycle(this.idleFreeSpinMessageKeys, this.cycleMessage);
    }
    handleAvailableWin() {
        this.setSingle(this.singleAvailableWinMessage(SlingoLang.PURCHASE_SPIN + RandomUtil.nextInt(0, 2)));
    }
    handleScatterWinJoker() {
        this.setSingle(this.singleScatterWinJokerMessage());
    }
    handleScatterWinCoin() {
        this.setSingle(this.singleScatterWinCoinMessage());
    }
    handleDevilAnimation() {
        this.setSingle(this.singleDevilMessage());
    }
    handleCherubAnimation() {
        this.setSingle(this.singleMessage(SlingoLang.CHERUB));
    }
    handleSlingo() {
        this.setSingle(this.singleMessage(SlingoLang.SLINGO_AWARD + this.model.getSlingosAwarded()));
    }
    handleSlingoComplete() {
        let patternWin = this.model.getCurrentPatternWin();
        if (this.model.getResultData().isFullHouse()) {
            this.clearMessages();
        }
        else if (patternWin > 0) {
            this.setSingle(this.singleSlingoCompleteMessage(patternWin));
        }
    }
    handleComplete() {
        let result = this.model.getResultData();
        if (result.hasNoWin()) {
            this.setSingle(this.singleMessage(SlingoLang.RESULT_NO_WIN + RandomUtil.nextInt(0, 3)));
        }
        else {
            if (result.isFullHouse()) {
                this.setSingle(this.singleMessage(SlingoLang.RESULT_FULL_CARD));
            }
            else if (result.hasWin25x()) {
                this.setSingle(this.singleMessage(SlingoLang.RESULT_WIN_25X));
            }
            else if (result.isBigWin()) {
                this.setSingle(this.singleMessage(SlingoLang.RESULT_WIN_7X));
            }
            else if (result.hasWin2x()) {
                this.setSingle(this.singleMessage(SlingoLang.RESULT_WIN_2X));
            }
        }
    }
    handleStandardSpinRecovery() {
        if (this.model.isSpin()) {
            this.handleStandardSpin();
        }
        this.updateGameId();
    }
    handleFreeSpinRecovery() {
        if (this.model.isSpin()) {
            this.handleFreeSpin();
        }
        this.updateGameId();
    }
    handleMessageTimerUpdated() {
        if (this.currentMessage.hasNext()) {
            this.updateMessage(this.currentMessage);
        }
        else {
            if (this.cycling) {
                let index = this.messageCycleIndex + 1;
                this.messageCycleIndex = index < this.messagesToCycle.length ? index : 0;
                this.nextMessage();
            }
            else {
                this.messageTimer.stop();
            }
        }
    }
    setSingle(message) {
        this.messageTimer.stop();
        this.cycling = false;
        this.updateMessage(message);
        this.messageTimer.start();
    }
    setCycle(messages, action, updateMessage = true) {
        this.messageTimer.stop();
        this.cycling = true;
        this.messagesToCycle = messages;
        this.messageCycleIndex = 0;
        this.cycleMessageHandler = new EventHandler(action, this);
        this.messageTimer.start();
        if (updateMessage) {
            this.nextMessage();
        }
    }
    clearMessages() {
        this.messageTimer.stop();
        this.cycling = false;
        this.messagesToCycle = [];
        this.cycleMessageHandler = null;
        this.updateMessage(new MessageData("", 10));
    }
    nextMessage() {
        let key = this.messagesToCycle[this.messageCycleIndex];
        if (this.cycleMessageHandler) {
            this.cycleMessageHandler.call(key);
        }
    }
    updateMessage(message) {
        this.currentMessage = message;
        if (Service.isReplay()) {
            this.messageField.setText(this.langManager.getLang("GameLang.OPTIONS_DGH"), true);
            this.messageField.alpha = 1;
        }
        else {
            this.messageField.alpha = 0;
            this.messageField.setText(this.currentMessage.next(), true);
            gsap.to(this.messageField, { alpha: 1, duration: 0.5 });
        }
    }
    cycleMessage(key) {
        this.updateMessage(this.createMessageData(key));
    }
    cycleIdleMessage(key) {
        let gameConfig = this.model.getGameConfig();
        let maxWin = this.formatCurrency(this.model.getMaxWin());
        let scatter5 = this.formatCurrency(this.model.getScatterPrize(5));
        let scatter4 = this.formatCurrency(this.model.getScatterPrize(4));
        let scatter3 = this.formatCurrency(this.model.getScatterPrize(3));
        let params = [
            new KeyValuePair(SlingoLang.PARAM_SPINS, gameConfig.getNumberOfSpins()),
            new KeyValuePair(CommonLang.PARAM_AMOUNT, maxWin),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_5, scatter5),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_4, scatter4),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_3, scatter3)
        ];
        this.updateMessage(this.createMessageData(key, params));
    }
    cycleIdleStandardSpinMessage(key) {
        let config = this.model.getGameProgress();
        if (config) {
            let params = [
                new KeyValuePair(SlingoLang.PARAM_SPINS, config.getSpinsRemaining())
            ];
            this.updateMessage(this.createMessageData(key, params));
        }
    }
    singleMessage(key) {
        return this.createMessageData(key);
    }
    singleStandardSpinMessage() {
        let gameConfig = this.model.getGameConfig();
        let progress = this.model.getGameProgress();
        let standardSpins = progress.getStandardSpins();
        let numberOfSpins = standardSpins > 0 ? standardSpins : gameConfig.getNumberOfSpins();
        let currentSpin = numberOfSpins - progress.getSpinsRemaining();
        let params = [
            new KeyValuePair(SlingoLang.PARAM_SPIN, currentSpin),
            new KeyValuePair(SlingoLang.PARAM_SPINS, numberOfSpins)
        ];
        return this.createMessageData(SlingoLang.STANDARD_SPIN, params);
    }
    singleScatterWinJokerMessage() {
        let result = this.model.getSpinResult();
        let win = result.getJokerWin();
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(win.getAmount()))
        ];
        let key = SlingoLang.SYMBOL_WIN + win.getNumberOfSymbols() + SlingoLang.SYMBOL_WIN_JOKER + RandomUtil.nextInt(0, 2);
        return this.createMessageData(key, params);
    }
    singleScatterWinCoinMessage() {
        let result = this.model.getSpinResult();
        let win = result.getCoinWin();
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(win.getAmount()))
        ];
        let key = SlingoLang.SYMBOL_WIN_COIN + RandomUtil.nextInt(0, 2);
        return this.createMessageData(key, params);
    }
    singleDevilMessage() {
        return this.createMessageData(SlingoLang.DEVIL + RandomUtil.nextInt(0, 2));
    }
    singleAvailableWinMessage(key) {
        let availableWin = this.model.getAvailableWin();
        let params = [
            new KeyValuePair(SlingoLang.PARAM_CELLS, availableWin.getKey()),
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(availableWin.getWinAmount()))
        ];
        return this.createMessageData(key, params);
    }
    singleSlingoCompleteMessage(patternWin) {
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(patternWin))
        ];
        return this.createMessageData(SlingoLang.SLINGO_COMPLETE, params);
    }
    createMessageKeys(key, total) {
        let keys = [];
        for (let i = 0; i < total; i++) {
            keys.push(key + i);
        }
        return keys;
    }
    createMessageData(key, params) {
        return new MessageData(this.langManager.getLang(key, params), this.maxLength);
    }
    handleResize(event) {
        const transFormedScale = this.getTransformedScale(this, this.scale);
        const renderView = this.renderer.getView();
        const renderWidth = parseInt(renderView.style.width, 10) || renderView.width;
        const distanceToTop = 0 - this.getGlobalPosition().y;
        const distanceToLeft = 0 - this.gameId?.getGlobalPosition().x;
        const distanceToRight = renderWidth - this.clock?.getGlobalPosition().x;
        //Frustratingly, you can't detect fullscreen if the game is in an iframe. The only solution is to force the offset at all times.
        const fullScreenOffset = this.uiProperties.fullScreenOffset; // document.fullscreenElement ? this.uiProperties.fullScreenOffset : 0;
        this.y += distanceToTop / transFormedScale.y;
        if (this.gameId)
            this.gameId.x += distanceToLeft / transFormedScale.x + this.uiProperties.fieldOffset + fullScreenOffset;
        if (this.clock)
            this.clock.x += distanceToRight / transFormedScale.x - this.uiProperties.fieldOffset - fullScreenOffset;
        if (this.background)
            this.background.width = renderWidth / transFormedScale.x;
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.uiProperties = this.uiProperties;
    }
}
//# sourceMappingURL=MessageBar.js.map